import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getRouteTuple, Lang } from "@/utils/routes";

// Types
type LinkToProps = {
  // Route key (e.g., 'features.scheduling', 'resources.whitepapers') or direct path
  to: string;
  // Link content
  children: ReactNode;
  // Additional CSS classes
  className?: string;
  // Force a specific language instead of using current language
  language?: Lang;
  // Additional props
  [key: string]: unknown;
};

/**
 * LinkTo - A component for internal links using a predefined route map
 *
 * This component handles language-specific routes automatically based on the current language.
 * It maps route keys like 'resources.whitepapers' to the appropriate URL path for the current language.
 */
export function LinkTo({
  to,
  children,
  className = "",
  language,
  ...props
}: LinkToProps) {
  const { t, i18n } = useTranslation(["common", "navigation"]);
  const currentLanguage = language || (i18n.language as Lang) || "sv";
  const normalizedLanguage = currentLanguage.startsWith("en") ? "en" : "sv";

  // Try to get localized path using the centralized route system
  let path: string;

  // First, check if 'to' is a route key (e.g. 'features', 'products', etc.)
  const routeTuple = getRouteTuple(to);

  if (routeTuple) {
    // Use the route from our mapping
    path = normalizedLanguage === "en" ? routeTuple[1] : routeTuple[0];
  } else {
    // Treat as direct path
    path = to;

    // Handle language prefix for direct paths in English
    if (
      normalizedLanguage === "en" &&
      !path.startsWith("/en/") &&
      !path.startsWith("http")
    ) {
      if (path === "/") {
        path = "/en";
      } else if (path.startsWith("/")) {
        path = `/en${path}`;
      } else {
        path = `/en/${path}`;
      }
    }
  }

  return (
    <Link to={path} className={className} {...props}>
      {children}
    </Link>
  );
}

export default LinkTo;
